import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Zap, Users, CheckCircle2, Hourglass, Timer } from "lucide-react";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import downloadBlob from "../../utils/downloadBlob";
import { exportActivation } from "../../services/api";
import { fetchActivationReport, fetchActivationMembers } from "../../store/reportSlices/performanceReportSlice";
import { StatCard, ReportHeader, ErrorBanner, FilterActions, DrillModal, CountLink, inputCls, num, pct, fmtDate, customerName } from "./ReportUI";

const buildPayload = (f) => ({ from_date: f.fromDate || null, to_date: f.toDate || null });
const defaultFilters = () => ({ fromDate: "", toDate: "" });

const DRILL_COLUMNS = [
    { header: "Customer", render: (r) => <span className="font-medium text-gray-800">{customerName(r)}</span> },
    { header: "Contact", render: (r) => <>{r.email || "—"}<br /><span className="text-xs text-gray-400">{r.mobile}</span></> },
    { header: "Latest Active Membership", render: (r) => fmtDate(r.first_activation) },
    { header: "First Benefit Used", render: (r) => fmtDate(r.first_txn) },
    { header: "Days to first benefit", render: (r) => (r.days_to_first ?? "—") },
];

const RateTile = ({ w, onClick }) => (
    <button type="button" onClick={onClick} disabled={!w.activated}
        className="bg-white rounded-xl shadow p-5 text-left hover:shadow-md transition disabled:cursor-default">
        <p className="text-xs text-gray-500">Used first benefit within</p>
        <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-gray-800">{w.days} days</span>
            <span className="ml-auto text-3xl font-bold text-[#d89005]">{pct(w.rate)}</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 mt-3 overflow-hidden">
            <div className="h-full bg-[#f4a406]" style={{ width: `${w.rate || 0}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-2">
            {w.eligible
                ? `${num(w.activated)} of ${num(w.eligible)} active paid members used a benefit within ${w.days} days`
                : "No active paid members found"}
        </p>
    </button>
);

export default function ActivationRate() {
    useDocumentTitle("Activation Rate | Reports");
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((s) => s.performanceReport.activation);

    const [showFilters, setShowFilters] = useState(false);
    const [draft, setDraft] = useState(defaultFilters());
    const [applied, setApplied] = useState(defaultFilters());
    const [drill, setDrill] = useState(null); // { state, title }
    const [exporting, setExporting] = useState(false);

    const load = () => dispatch(fetchActivationReport(buildPayload(applied)));
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dispatch, applied]);

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) return toastr.error("From date cannot be after To date");
        setApplied({ ...draft });
    };
    const handleReset = () => { const f = defaultFilters(); setDraft(f); setApplied(f); };

    const doExport = async (state = "") => {
        setExporting(true);
        try { downloadBlob(await exportActivation({ ...buildPayload(applied), state: state || null }), "activation-rate.csv"); }
        catch { toastr.error("Unable to export. Please try again."); }
        finally { setExporting(false); }
    };

    const s = data?.summary || { total: 0, activated_ever: 0, not_activated: 0, avg_days: null, windows: [] };
    const cohorts = data?.cohorts || [];
    const openDrill = (state, title) => setDrill({ state, title });

    return (
        <div className="w-full space-y-4">
            <ReportHeader icon={Zap} title="Activation Rate"
                subtitle="Percentage of active paid members who use their first benefit within 30, 60 or 90 days of their latest active membership."
                activeFilters={(applied.fromDate ? 1 : 0) + (applied.toDate ? 1 : 0)}
                showFilters={showFilters} onToggleFilters={() => setShowFilters((v) => !v)}
                onExport={() => doExport()} exporting={exporting} exportDisabled={loading || !s.total} />

            {showFilters && (
                <form onSubmit={handleApply} className="bg-white rounded-xl shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Active Membership From</label>
                            <input type="date" value={draft.fromDate} onChange={(e) => setDraft({ ...draft, fromDate: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Active Membership To</label>
                            <input type="date" value={draft.toDate} min={draft.fromDate || undefined} onChange={(e) => setDraft({ ...draft, toDate: e.target.value })} className={inputCls} />
                        </div>
                    </div>
                    <FilterActions onReset={handleReset} />
                </form>
            )}

            <ErrorBanner message={error} onRetry={load} />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Active Paid Members" value={num(s.total)} sub="unique customers" accent="blue" onClick={() => openDrill("", "All paid members")} />
                <StatCard icon={CheckCircle2} label="Used a Benefit" value={num(s.activated_ever)} sub={s.total ? `${((s.activated_ever / s.total) * 100).toFixed(1)}% of members` : undefined} accent="green" onClick={() => openDrill("activated", "Activated members")} />
                <StatCard icon={Hourglass} label="Not Used a Benefit Yet" value={num(s.not_activated)} accent="rose" onClick={() => openDrill("not_activated", "Not Used a Benefit Yet")} />
                <StatCard icon={Timer} label="Average Days to First Use" value={s.avg_days ?? "—"} accent="amber" />
            </div>

            {loading && !data ? (
                <ShimmerLoader />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {s.windows.map((w) => <RateTile key={w.days} w={w} onClick={() => openDrill(`within_${w.days}`, `Activated within ${w.days} days`)} />)}
                </div>
            )}

            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <div className="px-4 py-3 border-b"><h4 className="font-semibold text-gray-800">Monthly Active Memberships</h4>
                    <p className="text-xs text-gray-500">
                        Members grouped by the month their latest active paid membership started.
                        Recent members are included in all 30 / 60 / 90-day denominators.
                    </p></div>
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">Cohort</th>
                            <th className="px-4 py-3 text-right">Members</th>
                            <th className="px-4 py-3 text-right">Used a Benefit</th>
                            {[30, 60, 90].map((d) => <th key={d} className="px-4 py-3 text-right">{d}-day rate</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data && <tr><td colSpan={6}><ShimmerLoader /><ShimmerLoader /></td></tr>}
                        {!loading && cohorts.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-10">No paid members found for the selected filters.</td></tr>}
                        {cohorts.map((c) => (
                            <tr key={c.cohort} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{c.cohort}</td>
                                <td className="px-4 py-3 text-right">{num(c.total)}</td>
                                <td className="px-4 py-3 text-right">{num(c.activated_ever)}</td>
                                {c.windows.map((w) => (
                                    <td key={w.days} className="px-4 py-3 text-right" title={w.eligible ? `${w.activated} of ${w.eligible}` : "Not enough time has passed"}>
                                        {pct(w.rate)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {drill && (
                <DrillModal title={drill.title} subtitle="First benefit means the first completed redemption after the latest active membership started."
                    listKey="activationMembers" thunk={fetchActivationMembers}
                    payload={{ ...buildPayload(applied), state: drill.state || null }}
                    columns={DRILL_COLUMNS} onExport={() => doExport(drill.state)} exporting={exporting} onClose={() => setDrill(null)} />
            )}
        </div>
    );
}